import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, RefreshCw, Loader2, ArrowDownUp } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RecentMessages = ({ botId }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMessages = useCallback(async () => {
    try {
      const params = botId ? `?bot_id=${botId}` : '';
      const { data } = await axios.get(`${BACKEND_URL}/api/bot/recent-messages${params}`, { withCredentials: true });
      setMessages(data.messages || []);
    } catch (e) {
      console.error('Failed to fetch messages:', e);
    } finally {
      setLoading(false);
    }
  }, [botId]);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 15000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  const formatTime = (t) => {
    if (!t) return '';
    const d = new Date(t);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Hace un momento';
    if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)} h`;
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm" data-testid="recent-messages-panel">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-950">Mensajes Recientes</h3>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{messages.length}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchMessages} data-testid="refresh-messages-btn" className="text-gray-500 hover:text-gray-700">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-indigo-500 animate-spin" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12 px-4">
            <ArrowDownUp className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">Aun no hay mensajes.</p>
            <p className="text-xs text-gray-400 mt-1">Los mensajes de WhatsApp apareceran aqui.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {messages.map((msg, i) => (
              <div key={i} className="px-5 py-3 hover:bg-gray-50 transition-colors" data-testid={`message-item-${i}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium text-gray-800">{msg.sender_name || 'Desconocido'}</span>
                  <span className="text-xs text-gray-400">{formatTime(msg.timestamp)}</span>
                </div>
                <div className="flex gap-2 mb-1">
                  <span className="text-xs text-gray-400 shrink-0 mt-0.5">Entrada:</span>
                  <p className="text-sm text-gray-600 line-clamp-2">{msg.incoming_message}</p>
                </div>
                <div className="flex gap-2">
                  <span className="text-xs text-indigo-400 shrink-0 mt-0.5">Bot:</span>
                  <p className="text-sm text-indigo-700 line-clamp-2">{msg.bot_response}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentMessages;
